import IntroSection from '../Common/IntroSection';
import VisitUsSection from '../Common/VisitUsSection';
import BookVisitSection from '../Common/BookVisitSection';
import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import AnimatedSection from '../Common/AnimatedSection';
import { Link } from 'react-router';

const DentalFAQ = () => {
  const [openQuestion, setOpenQuestion] = useState('q1');
  
  const faqs = [
    {
      id: 'q1',
      question: 'How often should I visit the dentist?',
      answer: 'It\'s recommended to visit the dentist every six months for routine cleanings and exams. However, some patients may require more frequent visits depending on their oral health needs.'
    },
    {
      id: 'q2',
      question: 'Are dental X-rays safe?',
      answer: 'Yes, dental X-rays are safe. We use digital X-rays that emit very low levels of radiation, significantly lower than traditional film X-rays, and only when necessary to ensure your safety.'
    },
    {
      id: 'q3',
      question: 'What should I do if I have a dental emergency?',
      answer: 'Contact our office immediately if you experience a dental emergency such as a toothache, knocked-out tooth, or broken tooth. We offer same-day appointments to provide urgent care.'
    },
    {
      id: 'q4',
      question: 'Do you accept insurance?',
      answer: 'Yes, we accept most major dental insurance plans and will gladly help you understand and maximize your benefits. We also offer flexible payment options for uninsured patients.'
    },
    {
      id: 'q5',
      question: 'At what age should my child first see a dentist?',
      answer: 'The American Dental Association recommends that children have their first dental visit by age 1 or within six months of their first tooth erupting.'
    },
    {
      id: 'q6',
      question: 'What can I do to prevent cavities?',
      answer: 'Brush twice a day with fluoride toothpaste, floss daily, limit sugary snacks and drinks, and visit your dentist regularly for professional cleanings and checkups.'
    },
    {
      id: 'q7',
      question: 'What is the best way to whiten my teeth?',
      answer: 'Professional teeth whitening performed by your dentist is the safest and most effective method. We offer in-office treatments and take-home kits to suit your preference.'
    },
    {
      id: 'q8',
      question: 'Is gum disease reversible?',
      answer: 'Gum disease in its early stage (gingivitis) is reversible with professional treatment and improved oral hygiene. Advanced stages require ongoing care to manage symptoms and prevent further damage.'
    },
    {
      id: 'q9',
      question: 'Do you offer sedation for nervous patients?',
      answer: 'Yes, we offer sedation options such as nitrous oxide to help you feel relaxed and comfortable during your visit. Talk to us about your concerns, and we\'ll customize your care accordingly.'
    },
    {
      id: 'q10',
      question: 'How do I know if I need a dental filling or a crown?',
      answer: 'Fillings are used for small areas of decay, while crowns are recommended for more extensive damage or weakened teeth. We\'ll assess your tooth and recommend the most appropriate treatment during your visit.'
    }
  ];

  const toggleQuestion = (id) => {
    setOpenQuestion(openQuestion === id ? null : id);
  };

  return (
    <div className="mx-auto px-4 sm:px-6 lg:px-8 xl:px-20 py-16 bg-gray-50">
      <div className="text-center mb-12">
        <h2 className="text-3xl font-semibold text-gray-800 mb-3">Frequently Asked Questions</h2>
        <p className="text-teal-600">Everything you need to know about your dental care</p>
      </div>

      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div
            key={faq.id}
            className="opacity-0 animate-fade-in"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm 
                          hover:shadow-md transition-shadow duration-300 bg-white">
              <button
                className="w-full px-6 py-4 text-left flex justify-between items-center 
                           hover:bg-gray-50 transition-colors duration-300"
                onClick={() => toggleQuestion(faq.id)}
              >
                <span className="text-lg font-medium text-teal-600">{faq.question}</span>
                <span className={`flex items-center justify-center w-8 h-8 rounded-full 
                                ${openQuestion === faq.id ? 'bg-teal-600' : 'bg-lime-500'} 
                                transition-colors duration-300`}>
                  <ChevronDown
                    className={`w-5 h-5 text-white transition-transform duration-300 
                             ${openQuestion === faq.id ? 'rotate-180' : ''}`}
                  />
                </span>
              </button>

              <div
                className={`transition-all duration-300 ease-in-out 
                           ${openQuestion === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
              >
                <div className="px-6 pb-4 pt-2">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="mt-12 text-center">
        <p className="text-gray-500 mb-4">Still have questions?</p>
        <Link
          to='https://wa.me/+918401749379'
          target='_blank' 
          className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-6 rounded-full shadow-md transition duration-300">
          Contact Us
        </Link>
      </div>
    </div>
  );
};

const FAQ = () => {
  useEffect(() => {
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Hero Section */}
      <AnimatedSection id="intro" delay={200}>
        <IntroSection 
            title={"FREQUENTLY ASKED QUESTIONS"} 
            about={"Learn about our practice, your dental health, and how to get started!"}/>
      </AnimatedSection>

      {/* Main Content */}
      <main className="w-full">
        <AnimatedSection id="faq" delay={0}>
          <DentalFAQ />
        </AnimatedSection>

        <AnimatedSection id="visit" delay={200} className="bg-gray-50">
          <VisitUsSection />
        </AnimatedSection>

        <AnimatedSection id="book" delay={400}>
          <BookVisitSection />
        </AnimatedSection>
      </main>
    </div>
  );
};

export default FAQ;